function getOccurrenceCount(source: string, character: string): number {
  const matches = source.match(new RegExp(`\\${character}`, 'g'));

  if (matches) {
    return matches.length;
  }

  return 0;
}

function testOnlyContainsValidCharacters(value: string, decimalSeparator: string, thousandsSeparator: string) {
  const separatorPattern = thousandsSeparator === ' '
    ? '\\s'
    : `\\${thousandsSeparator}`;

  const pattern = new RegExp(`^[0-9${decimalSeparator}${separatorPattern}]*$`);

  return pattern.test(value);
}

function testAreThousandSeparatorsValid(integerString: string, thousandsSeparator: string) {
  if (!integerString.includes(thousandsSeparator)) {
    return true;
  }

  const [firstGroup, ...groupsRequiringThreeDigits] = integerString.split(thousandsSeparator);

  const isFirstGroupValid = firstGroup.length <= 3 && firstGroup.length > 0;

  const areNonFirstGroupsValid = groupsRequiringThreeDigits.every((p) => p.length === 3);

  return isFirstGroupValid && areNonFirstGroupsValid;
}

export default function parseMoneyInput(rawValue: string, thousandsSeparator: string, decimalSeparator: string) {
  let value = rawValue.trim()
    .replace(/^0+/, ''); // Strip leading zeros

  if (value === '') {
    return {
      isValid: true,
      isEmpty: true,
      amount: 0,
    };
  }

  try {
    const isOnlyContainingValidCharacters = testOnlyContainsValidCharacters(value, decimalSeparator, thousandsSeparator);

    if (!isOnlyContainingValidCharacters) {
      throw new Error('Invalid characters present');
    }

    if (!/^[0-9]/.test(value)) {
      throw new Error('First characer is non-numeric');
    }

    const decimalSeparatorCount = getOccurrenceCount(value, decimalSeparator);

    if (decimalSeparatorCount > 1) {
      throw new Error('More than one decimal separator present');
    }

    if (decimalSeparatorCount === 0) {
      value += `${decimalSeparator}`;
    }

    const [integerString, decimalString] = value.split(decimalSeparator);

    const cleanedDecimalString = decimalString.replace(/0+$/, ''); // Strip extraneous zeros

    const isThousandSeparatorValid = testAreThousandSeparatorsValid(integerString, thousandsSeparator);

    if (!isThousandSeparatorValid) {
      throw new Error('Invalid thousands separator usage');
    }

    if (cleanedDecimalString.length > 2) {
      throw new Error('Too many decimal digits present');
    }

    if (getOccurrenceCount(cleanedDecimalString, thousandsSeparator)) {
      throw new Error('Thousands separator found in decimal value');
    }

    const cleanedIntegerString = integerString.replaceAll(thousandsSeparator, ''); // Strip extraneous thousands separators

    return {
      isValid: true,
      isEmpty: false,
      amount: parseFloat(`${cleanedIntegerString}.${cleanedDecimalString}`),
    };
  } catch (e) {
    return {
      isValid: false,
      isEmpty: false,
      amount: NaN,
    };
  }
}
